import {NgModule} from '@angular/core';
import {RouterModule, Routes} from '@angular/router';
import {UserIsAdminGuard} from "./guards/user-is-admin-guard";
import {LogoutPage} from "./pages/auth/logout/logout.page";
import {UserIsNotAdminPageComponent} from "./pages/user-is-not-admin-page/user-is-not-admin-page.component";
import {DrawWinnerPageComponent} from "./pages/draw-winner/game-admin-draw-winner/draw-winner-page.component";
import {
  GameAdminManageGameComponent
} from "./pages/manage-game/game-admin-manage-game/game-admin-manage-game.component";
import {PlayerSearchComponent} from "./pages/player-search/game/player-search.component";
import {GameSelectedGuard} from "./guards/game-selected-guard";
import {
  CrossGameManageUsersComponent
} from "./pages/manage-users/cross-game-manage-users/cross-game-manage-users.component";
import {RecoverPasswordComponent} from "./pages/recover-password/recover-password.component";
import {UpdatePasswordComponent} from "./pages/update-password/update-password.component";
import {UpdateEmailAddressComponent} from "./pages/update-email-address/update-email-address.component";
import {AuditingComponent} from "./pages/auditing/auditing.component";
import {ErrorDebuggingComponent} from "./pages/error-debugging/error-debugging.component";
import {PermissionGuard} from "./guards/permission-guard";
import {PermissionTypes} from "./enum/PermissionTypes";
import {NotAuthorizedComponent} from "./pages/not-authorized/not-authorized.component";
import {TestingPageComponent} from "./pages/testing-page/testing-page.component";
import {NewGameComponent} from "./pages/new-game/new-game.component";
import {GameRulesEditorComponent} from "./pages/game-rules-editor/game-rules-editor.component";
import {HowToPlayEditorComponent} from "./pages/how-to-play-editor/how-to-play-editor.component";
import {ActiveGameInstancesComponent} from "./pages/active-game-instances/active-game-instances.component";
import {LoginGuard} from "./guards/login.guard";
import {AdminAuthCallbackPage} from "./pages/auth/admin-auth-callback/admin-auth-callback.page";
import {LoginComponent} from "./pages/auth/login/login.component";
import {CausableAdminGuard} from "./guards/causable-admin-guard";
import {
  FrequentlyAskedQuestionsComponent
} from "./pages/frequently-asked-questions/frequently-asked-questions.component";
import {LandingComponent} from "./pages/landing/landing.component";
import {CharityAdminGuard} from "./guards/charity-admin.guard";
import {CrossGameSupportDeskComponent} from "./pages/support-desk/cross-game-support-desk.component";
import {ManagePlayerComponent} from "./pages/manage-player/manage-player.component";
import {CheckForSingleGameAccessResolver} from "./resolvers/check-for-single-game-access.resolver";
import {ProfanityFilterManagerComponent} from "./pages/profanity-filter-manager/profanity-filter-manager.component";
import {ErrorCategoryManagementComponent} from "./pages/error-category-management/error-category-management.component";
import {SendGridEventTableComponent} from "./pages/send-grid-event-table/send-grid-event-table.component";
import {
  GameInstancePlayerEmailsComponent
} from "./pages/game-instance-player-emails/game-instance-player-emails.component";
import {GlobalPlayerSearchComponent} from "./pages/player-search/global/global-player-search.component";
import {GameAdminSupportDeskComponent} from "./pages/support-desk/game-admin-support-desk.component";
import {
  ManageGlobalPermissionsComponent
} from "./pages/manage-roles-permission/manage-global-permissions/manage-global-permissions.component";
import {
  CrossGameActiveDrawLandingComponent
} from "./pages/cross-game-active-draw-landing/cross-game-active-draw-landing.component";
import {
  CrossGameManageGameComponent
} from "./pages/manage-game/cross-game-manage-game/cross-game-manage-game.component";
import {
  GameAdminPreferencesComponent
} from "./pages/notification-preferences/game-admin-preferences/game-admin-preferences.component";
import {
  CrossGamePreferencesComponent
} from "./pages/notification-preferences/cross-game-preferences/cross-game-preferences.component";
import {
  CrossGameCharityManagementComponent
} from "./pages/manage-charities/cross-game-charity-management/cross-game-charity-management.component";
import {
  GameAdminCharityManagementComponent
} from "./pages/manage-charities/game-admin-charity-management/game-admin-charity-management.component";
import {CharityComponent} from "./pages/charity/charity.component";
import {GameAdminReportingComponent} from "./pages/reports/game-admin-reporting/game-admin-reporting.component";
import {CrossGameReportingComponent} from "./pages/reports/cross-game-reporting/cross-game-reporting.component";
import {
  GameAdminManageUsersComponent
} from "./pages/manage-users/game-admin-manage-users/game-admin-manage-users.component";
import {LandingPageRedirectGuard} from "./guards/landing-page-redirect.guard";
import {AddNewRoleComponent} from "./pages/manage-roles-permission/add-new-role/add-new-role.component";
import {
  CrossGameAdminDrawWinnerComponent
} from "./pages/draw-winner/cross-game-admin-draw-winner/cross-game-admin-draw-winner.component";
import {
  GameAdminWinnerPayoutsComponent
} from "./pages/winner-payouts/game-admin-winner-payouts/game-admin-winner-payouts.component";
import {
  CrossGameWinnerPayoutsComponent
} from "./pages/winner-payouts/cross-game-winner-payouts/cross-game-winner-payouts.component";
import {
  GameAdminCharityPayoutsComponent
} from "./pages/charity-payouts/game-admin-charity-payouts/game-admin-charity-payouts.component";
import {
  CrossGameCharityPayoutsComponent
} from "./pages/charity-payouts/cross-game-charity-payouts/cross-game-charity-payouts.component";
import {ViewEditRoleComponent} from "./pages/manage-roles-permission/view-edit-role/view-edit-role.component";
import {SignalRConnectionsComponent} from "./pages/signal-r-connections/signal-r-connections.component";
import {LogLevelSwitchComponent} from "./pages/log-level-switch/log-level-switch.component";
import {
  PlayerEmailConfirmationsComponent
} from "./components/player-email-confirmations/player-email-confirmations.component";
import {AdminTermsComponent} from "./pages/admin-terms/admin-terms.component";
import {
  CreateBroadcastNotificationComponent
} from "./pages/create-broadcast-notification/create-broadcast-notification.component";
import {ManageNotificationsComponent} from "./pages/manage-broadcast-notifications/manage-notifications.component";
import {PlayerEmailHistoryComponent} from "./pages/player-email-history/player-email-history.component";
import {FileIntegrityDashboardComponent} from "./pages/file-integrity-dashboard/file-integrity-dashboard.component";
import {ManageRegionTaxRatesComponent} from "./pages/manage-region-tax-rates/manage-region-tax-rates.component";
import {
  CrossGamePaymentMethodsComponent
} from "./pages/payment-methods/cross-game-payment-methods/cross-game-payment-methods.component";
import {
  GameAdminPaymentMethodsComponent
} from "./pages/payment-methods/game-admin-payment-methods/game-admin-payment-methods.component";
import {FailedRefundsComponent} from "./pages/failed-refunds/failed-refunds.component";
import {UserDetailsComponent, UserDetailsRouteData} from "./pages/user-details/user-details.component";

const routes: Routes = [
  {
    path: '',
    title: 'Landing',
    canActivate: [LandingPageRedirectGuard],
    resolve: [CheckForSingleGameAccessResolver],
    component: LandingComponent
  },
  {
    path: 'not-admin',
    title: 'Not Admin',
    component: UserIsNotAdminPageComponent
  },
  {
    path: 'not-authorized',
    title: 'Not Authorized',
    component: NotAuthorizedComponent
  },
  {
    path: 'forgot-my-password',
    title: 'Recover Password',
    component: RecoverPasswordComponent
  },
  {
    path: 'auth/admin-auth-callback',
    canActivate: [LoginGuard],
    component: AdminAuthCallbackPage
  },
  {
    path: 'auth/login',
    canActivate: [LoginGuard],
    component: LoginComponent
  },
  {
    path: 'logout',
    title: 'Logout',
    canActivate: [UserIsAdminGuard],
    component: LogoutPage
  },
  {
    path: '',
    redirectTo: 'auth/login',
    pathMatch: 'full'
  },

  // admin required routes
  {
    path: '',
    canActivate: [UserIsAdminGuard],
    children: [
      {
        path: 'privacy-policy',
        title: 'Admin Terms of Service',
        component: AdminTermsComponent
      },
      {
        path: 'frequently-asked-questions',
        title: 'Frequently Asked Questions',
        component: FrequentlyAskedQuestionsComponent
      },
      {
        path: 'cross-game-charities',
        canActivate: [() => PermissionGuard(PermissionTypes.ViewCharities)],
        title: 'Charity Management',
        component: CrossGameCharityManagementComponent
      },
      {
        path: 'view-player-emails',
        title: 'Game Instance Player Emails',
        canActivate: [CausableAdminGuard],
        component: GameInstancePlayerEmailsComponent
      },
      {
        path: 'signal-r-connections',
        title: 'Signal R Connections',
        canActivate: [CausableAdminGuard],
        component: SignalRConnectionsComponent
      },
      {
        path: 'game-instance-game-emails',
        title: 'Game Instance Player Emails',
        canActivate: [CausableAdminGuard],
        component: GameInstancePlayerEmailsComponent
      },
      {
        path: 'new-game',
        title: 'New Game',
        canActivate: [CausableAdminGuard],
        component: NewGameComponent,
        children: [
          {
            path: ':/newGameId',
            component: NewGameComponent
          },
        ]
      },
      {
        path: 'profanity-filter-manager',
        title: 'Profanity Filter Manager',
        canActivate: [CausableAdminGuard],
        component: ProfanityFilterManagerComponent
      },
      {
        path: 'error-category-management',
        title: 'Error Category Management',
        canActivate: [() => PermissionGuard(PermissionTypes.ViewSystemErrorMessages)],
        component: ErrorCategoryManagementComponent
      },

      {
        path: 'testing-page',
        title: 'Testing Page',
        component: TestingPageComponent,
        canActivate: [CausableAdminGuard]
      },
      {
        path: 'sendgrid-events',
        title: 'SendGrid Events',
        canActivate: [() => PermissionGuard(PermissionTypes.ViewSendgridData)],
        component: SendGridEventTableComponent
      },
      {
        path: 'cross-game-game-management',
        title: 'Manage Game',
        canActivate: [() => PermissionGuard(PermissionTypes.ViewGameSettings)],
        component: CrossGameManageGameComponent
      },
      {
        path: 'auditing',
        title: 'Auditing',
        canActivate: [() => PermissionGuard(PermissionTypes.ViewAuditing)],
        component: AuditingComponent
      },
      {
        path: 'error-debugging',
        title: 'Error Debugging',
        canActivate: [() => PermissionGuard(PermissionTypes.ViewAuditing)],
        component: ErrorDebuggingComponent
      },
      {
        path: 'cross-game-support-desk-search',
        title: 'Support Desk',
        component: CrossGameSupportDeskComponent,
        canActivate: [() => PermissionGuard(PermissionTypes.ViewSupportRequests)]
      },
      {
        path: 'cross-game-winner-payouts',
        title: 'Winner Payouts',
        canActivate: [() => PermissionGuard(PermissionTypes.ViewWinnerPayouts)],
        component: CrossGameWinnerPayoutsComponent
      },
      {
        path: 'cross-game-charity-payouts',
        title: 'Charity Payouts',
        canActivate: [() => PermissionGuard(PermissionTypes.ViewCharityPayouts)],
        component: CrossGameCharityPayoutsComponent
      },
      {
        path: 'update-password',
        title: 'Update Password',
        component: UpdatePasswordComponent
      },
      {
        path: 'update-email-address',
        title: 'Update Email Address',
        component: UpdateEmailAddressComponent
      },
      {
        path: 'charity/:charityId',
        title: 'Charity',
        canActivate: [CharityAdminGuard],
        component: CharityComponent
      },
      {
        path: 'cross-game-admin-reporting',
        canActivate: [() => PermissionGuard(PermissionTypes.ViewReports)],
        title: 'Reports',
        component: CrossGameReportingComponent
      },
      {
        path: 'global-player-search',
        title: 'System Player Search',
        canActivate: [() => PermissionGuard(PermissionTypes.ViewGlobalPlayerSearch)],
        component: GlobalPlayerSearchComponent
      },
      {
        path: 'email-history',
        title: 'Email History',
        canActivate: [() => PermissionGuard(PermissionTypes.ViewPlayerInfo)],
        component: PlayerEmailHistoryComponent
      },
      {
        path: 'manage-cross-game-users',
        canActivate: [() => PermissionGuard(PermissionTypes.ViewAdminUsers)],
        title: 'Manage Users',
        component: CrossGameManageUsersComponent
      },
      {
        path: 'cross-game-view-draw-landing',
        canActivate: [() => PermissionGuard(PermissionTypes.ViewGameSummaryReport)],
        title: 'Active Draws',
        component: CrossGameActiveDrawLandingComponent
      },
      {
        path: 'cross-game-notification-preferences',
        title: 'Notification Preferences',
        component: CrossGamePreferencesComponent
      },
      {
        path: 'manage-player/:playerId',
        title: 'Manage Game Global Player',
        canActivate: [() => PermissionGuard(PermissionTypes.ViewGlobalPlayerSearch)],
        component: ManagePlayerComponent
      },
      {
        path: 'manage-global-permissions',
        canActivate: [CausableAdminGuard],
        title: 'Manage Global Permissions',
        component: ManageGlobalPermissionsComponent
      },
      {
        path: 'cross-game-draw-winner',
        title: 'Draw Winner',
        canActivate: [() => PermissionGuard(PermissionTypes.DrawGameWinner)],
        component: CrossGameAdminDrawWinnerComponent
      },
      {
        path: 'add-new-role',
        canActivate: [CausableAdminGuard],
        title: 'Add New Role',
        component: AddNewRoleComponent
      },
      {
        path: 'view-edit-role/:roleId',
        title: 'View Edit Role',
        canActivate: [() => PermissionGuard(PermissionTypes.ModifyRolesAndPermissions)],
        component: ViewEditRoleComponent
      },
      {
        path: 'log-level-switch',
        title: 'Log Level Switch',
        canActivate: [CausableAdminGuard],
        component: LogLevelSwitchComponent
      },
      {
        path: 'player-email-confirmations',
        title: 'Player Email Confirmations',
        component: PlayerEmailConfirmationsComponent,
        canActivate: [() => PermissionGuard(PermissionTypes.ViewEmailConfirmations)]
      },
      {
        path: 'manage-broadcast-notifications',
        title: 'Manage Broadcast Notifications',
        component: ManageNotificationsComponent,
        canActivate: [() => PermissionGuard(PermissionTypes.ViewBroadcastNotifications)]
      },
      {
        path: 'create-broadcast-notification',
        title: 'Create Broadcast Notification',
        component: CreateBroadcastNotificationComponent,
        data: {allowScopeSelection: true},
        canActivate: [() => PermissionGuard(PermissionTypes.ModifyBroadcastNotifications)]
      },
      {
        path: 'edit-broadcast-notification/:id',
        title: 'Edit Broadcast Notification',
        component: CreateBroadcastNotificationComponent,
        data: {allowScopeSelection: true},
        canActivate: [() => PermissionGuard(PermissionTypes.ModifyBroadcastNotifications)]
      },
      {
        path: 'file-integrity-dashboard',
        title: 'File Integrity Dashboard',
        component: FileIntegrityDashboardComponent,
        canActivate: [CausableAdminGuard]
      },
      {
        path: 'manage-region-tax-rates',
        title: 'Manage Region Tax Rates',
        component: ManageRegionTaxRatesComponent,
        canActivate: [CausableAdminGuard]
      },
      {
        path: 'payment-methods',
        title: 'Payment Methods',
        canActivate: [() => PermissionGuard(PermissionTypes.ViewPlayerInfo)],
        component: CrossGamePaymentMethodsComponent
      },
      {
        path: 'failed-refunds',
        title: 'Failed Refunds',
        component: FailedRefundsComponent,
        canActivate: [() => PermissionGuard(PermissionTypes.ViewFailedRefunds)]
      },
      {
        path: 'cross-game-user-details/:userId',
        component: UserDetailsComponent,
        data: {
          backUrl: 'manage-cross-game-users',
          useGameContext: false
        } as UserDetailsRouteData
      }
    ]
  },
  // game and admin required routes
  {
    path: ':gameId',
    canActivate: [UserIsAdminGuard, GameSelectedGuard],
    children: [
      {
        path: 'manage-game-admin-users',
        canActivate: [() => PermissionGuard(PermissionTypes.ViewAdminUsers)],
        title: 'Manage Users',
        component: GameAdminManageUsersComponent
      },
      {
        path: 'game-admin-user-details/:userId',
        component: UserDetailsComponent,
        data: {
          backUrl: 'manage-game-admin-users',
          useGameContext: true
        } as UserDetailsRouteData
      },
      {
        path: 'game-admin-winner-payouts',
        title: 'Winner Payouts',
        canActivate: [() => PermissionGuard(PermissionTypes.ViewWinnerPayouts)],
        component: GameAdminWinnerPayoutsComponent
      },
      {
        path: 'game-admin-charity-payouts',
        title: 'Charity Payouts',
        canActivate: [() => PermissionGuard(PermissionTypes.ViewCharityPayouts)],
        component: GameAdminCharityPayoutsComponent
      },
      {
        path: 'game-admin-notification-preferences',
        title: 'Notification Preferences',
        component: GameAdminPreferencesComponent
      },
      {
        path: 'charity/:charityId',
        title: 'Charity',
        canActivate: [CharityAdminGuard],
        component: CharityComponent
      },
      {
        path: 'game-admin-reporting',
        canActivate: [() => PermissionGuard(PermissionTypes.ViewReports)],
        title: 'Reports',
        component: GameAdminReportingComponent
      },
      {
        path: 'draw-winner',
        title: 'Draw Winner',
        canActivate: [() => PermissionGuard(PermissionTypes.DrawGameWinner)],
        component: DrawWinnerPageComponent
      },
      {
        path: 'manage-game',
        title: 'Manage Game',
        canActivate: [() => PermissionGuard(PermissionTypes.ViewGameSettings)],
        component: GameAdminManageGameComponent
      },
      {
        path: 'manage-player/:playerId',
        title: 'Manage Game Players',
        canActivate: [() => PermissionGuard(PermissionTypes.ViewPlayerInfo)],
        component: ManagePlayerComponent
      },
      {
        path: 'manage-charities',
        canActivate: [() => PermissionGuard(PermissionTypes.ViewCharities)],
        title: 'Charity Management',
        component: GameAdminCharityManagementComponent
      },
      {
        path: 'active-game-instances',
        title: 'Current Draw',
        component: ActiveGameInstancesComponent
      },
      {
        path: 'player-search',
        title: 'Player Search',
        canActivate: [() => PermissionGuard(PermissionTypes.ViewPlayerInfo)],
        component: PlayerSearchComponent
      },
      {
        path: 'payment-methods',
        title: 'Payment Methods',
        canActivate: [() => PermissionGuard(PermissionTypes.ViewPlayerInfo)],
        component: GameAdminPaymentMethodsComponent
      },
      {
        path: 'edit-game-rules',
        title: 'Edit Game Rules',
        component: GameRulesEditorComponent,
        canActivate: [() => PermissionGuard(PermissionTypes.ModifyGameSettings)]
      },
      {
        path: 'edit-how-to-play',
        title: 'Edit How To Play',
        component: HowToPlayEditorComponent,
        canActivate: [() => PermissionGuard(PermissionTypes.ModifyGameSettings)]
      },
      {
        path: 'game-admin-support-desk-search',
        title: 'Support Desk',
        component: GameAdminSupportDeskComponent,
        canActivate: [() => PermissionGuard(PermissionTypes.ViewSupportRequests)]
      },
      {
        path: 'manage-broadcast-notifications',
        title: 'Broadcast Notifications',
        component: ManageNotificationsComponent,
        canActivate: [() => PermissionGuard(PermissionTypes.ViewBroadcastNotifications)]
      },
      {
        path: 'create-broadcast-notification',
        title: 'Create Broadcast Notification',
        component: CreateBroadcastNotificationComponent,
        data: {allowScopeSelection: false},
        canActivate: [() => PermissionGuard(PermissionTypes.ModifyBroadcastNotifications)]
      },
      {
        path: 'edit-broadcast-notification/:id',
        title: 'Edit Broadcast Notification',
        component: CreateBroadcastNotificationComponent,
        data: {allowScopeSelection: false},
        canActivate: [() => PermissionGuard(PermissionTypes.ModifyBroadcastNotifications)]
      },
      {
        path: 'frequently-asked-questions',
        title: 'Frequently Asked Questions',
        component: FrequentlyAskedQuestionsComponent
      },
    ]
  }
];

// initialNavigation: "disabled" stops navigation until we are ready (re-enabled in app component when org and auth config have been set up)
@NgModule({
  imports: [RouterModule.forRoot(routes, {initialNavigation: "disabled"})],
  exports: [RouterModule]
})
export class AppRoutingModule {
}
